import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ExemptedStudent } from "../../../types/common";
import { StandAloneExemptedStudentsContext } from "../../context/EditQuizContext";
import { UseEditQuizStateProps } from "./useEditQuizState";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { getExemptedStudentsFromMap, hasExemptedStudentMapChanged } from "../../editQuizHelper";
import { ExemptionInfo } from "../../accommodation/exemptionHelper";
import { ExemptionSectionView } from "../../accommodation/ExemptionSection";

type UseStudentStateProps = Pick<
	UseEditQuizStateProps,
	| "quizInfo"
	| "api"
	| "uploadedExemptedStudentsMap"
	| "uploadedProfilesMap"
	| "setUploadedExemptedStudentsMap"
	| "quizType"
>;

/**
 * State and actions related to students, exemptions, standalone
 */
export default function useStudentState({
	api,
	quizInfo,
	uploadedExemptedStudentsMap,
	uploadedProfilesMap,
	setUploadedExemptedStudentsMap,
	quizType,
}: UseStudentStateProps) {
	const { getCourseStudents, getProctoringExemptionStudents, saveProctoringExemptionStudents } = api;
	const initialExemptedStudentsMapRef = useRef<Map<number, ExemptedStudent> | null>(null);
	const uploadedMapRef = useRef(uploadedExemptedStudentsMap); // so fetch exemption info doesnt re render on the accommodation step

	const [exemptionInfo, setExemptionInfo] = useState<ExemptionInfo>({
		nonExemptRoster: new Map(),
		exemptRoster: new Map(),
	});
	// Mirrors exemptionInfo synchronously so imperative reads (getExemptionSectionInfo, called via
	// ref right after a roster update) see the latest value instead of a stale pre-update React state,
	// since setExemptionInfo only takes effect on the next render.
	const exemptionInfoRef = useRef(exemptionInfo);
	const [accommodationView, setAccommodationView] = useState<ExemptionSectionView | undefined>(undefined);
	const [isDraftInvalid, setIsDraftInvalid] = useState(false);
	const saveDraftAccommodationRef = useRef<(() => ExemptionInfo) | null>(null);

	const registerAccommodationSaveFn = useCallback((saveFn: (() => ExemptionInfo) | null) => {
		saveDraftAccommodationRef.current = saveFn;
	}, []);

	const standAloneExemptedStudentsContext = useContext(StandAloneExemptedStudentsContext);
	const { studentList, exemptedStudents } = standAloneExemptedStudentsContext;

	const numExempted = useMemo(() => exemptionInfo.exemptRoster.size, [exemptionInfo.exemptRoster]);

	useEffect(() => {
		uploadedMapRef.current = uploadedExemptedStudentsMap;
	}, [uploadedExemptedStudentsMap]);

	const setInitialExemptionsRef = useCallback((rosters: { exemptRoster: Map<number, ExemptedStudent> }) => {
		// Only set it once (when it's null)
		if (initialExemptedStudentsMapRef.current === null) {
			initialExemptedStudentsMapRef.current = new Map(rosters.exemptRoster);
		}
	}, []);

	useEffect(() => {
		setUploadedExemptedStudentsMap(exemptionInfo.exemptRoster);
	}, [exemptionInfo.exemptRoster, setUploadedExemptedStudentsMap]);

	// if creating/ editing a standalone assessment, use the value from standAloneExemptedStudentsContext to set the default value for exemptionInfo

	// When creating a standalone assessment, the uploadedExemptedStudentsMap is set using setUploadedExemptedStudentsMap(exemptionInfo) in ExemptionSection.
	// uploadedExemptedStudentsMap is declared in CreateStandaloneAssessmentSection, and is used to create exemption emails for the standalone assessment.
	const initStandaloneExemptionInfo = useCallback(() => {
		if (quizType !== "standalone") return;

		setExemptionInfo((currentExemptionInfo) => {
			const studentListUserIds = new Set(studentList.map((student) => student.userId));
			const exemptedUserIds = new Set(exemptedStudents.map((student) => student.userId));
			const nonExemptedStudents = studentList.filter((student) => !exemptedUserIds.has(student.userId));

			// if this is false, the instructors added/removed students from the standalone student list. Update the exemptionInfo accordingly.
			const isStudentListEqualToStudentsInExemptionInfo =
				studentList.every(
					(student) =>
						currentExemptionInfo.exemptRoster.has(student.userId) ||
						currentExemptionInfo.nonExemptRoster.has(student.userId),
				) &&
				Array.from(currentExemptionInfo.exemptRoster.values())
					.concat(Array.from(currentExemptionInfo.nonExemptRoster.values()))
					.every((student) => studentListUserIds.has(student.userId));

			const needsUpdate =
				!isStudentListEqualToStudentsInExemptionInfo ||
				(currentExemptionInfo.exemptRoster.size === 0 && currentExemptionInfo.nonExemptRoster.size === 0);

			if (needsUpdate) {
				const nonExemptedStudentsMap = new Map<number, ExemptedStudent>();
				const exemptedStudentsMap = new Map<number, ExemptedStudent>();

				nonExemptedStudents.forEach((nonExemptedStudent) =>
					nonExemptedStudentsMap.set(nonExemptedStudent.userId, nonExemptedStudent),
				);
				exemptedStudents.forEach((exemptedStudent) => {
					exemptedStudentsMap.set(exemptedStudent.userId, exemptedStudent);
				});

				const nextExemptionInfo = {
					nonExemptRoster: nonExemptedStudentsMap,
					exemptRoster: exemptedStudentsMap,
				};
				setInitialExemptionsRef(nextExemptionInfo);
				exemptionInfoRef.current = nextExemptionInfo;
				return nextExemptionInfo;
			}
			return currentExemptionInfo;
		});
	}, [exemptedStudents, quizType, setInitialExemptionsRef, studentList]);

	const fetchAndSetExemptionInfo = useCallback(
		async (tempProfileMap: Map<number, ProctoringOptionProfileV2>) => {
			const quizId = quizInfo.quizId;

			if (!quizInfo.courseId) {
				console.warn("Missing courseId; cannot load exemptions.");
				return;
			}

			try {
				const studentRoster = await getCourseStudents(quizInfo);
				let exemptedUserIds: Omit<ExemptedStudent, "name">[] = [];
				if (quizId) {
					exemptedUserIds = await getProctoringExemptionStudents({ ...quizInfo, quizId });
				} else if (uploadedMapRef.current) {
					exemptedUserIds = Array.from(uploadedMapRef.current.values()).map(({ name: _name, ...rest }) => rest);
				}

				const exemptedStudentsMap = new Map(exemptedUserIds.map((e) => [e.userId, e]));

				// Categorize student roster by exemptions using a reduce to build the rosters
				const newExemptionInfo = (studentRoster ?? [])
					.map((s) => ({ userId: s.id, name: s.name }))
					.reduce(
						(prev, s) => {
							const exemptedStudent = exemptedStudentsMap.get(s.userId);

							if (exemptedStudent) {
								const studentProfileId = exemptedStudent.proctoringOptionProfileId;
								prev.exemptRoster.set(s.userId, {
									...s,
									type: exemptedStudent.type,
									accommodationNotes: exemptedStudent.accommodationNotes ?? undefined,
									proctoringOptionProfileId:
										studentProfileId && tempProfileMap.has(studentProfileId) ? studentProfileId : undefined,
								});
							} else {
								prev.nonExemptRoster.set(s.userId, s);
							}
							return prev;
						},
						{
							nonExemptRoster: new Map<number, ExemptedStudent>(),
							exemptRoster: new Map<number, ExemptedStudent>(),
						},
					);

				exemptionInfoRef.current = newExemptionInfo;
				setExemptionInfo(newExemptionInfo);
				setInitialExemptionsRef(newExemptionInfo);
				return true;
			} catch (err) {
				console.error("Failed to fetch exemption data:", err);
				return false;
			}
		},
		[getCourseStudents, getProctoringExemptionStudents, quizInfo, setInitialExemptionsRef],
	);

	const handleSaveExemptedStudentsNotStandalone = useCallback(
		async (quizId: string) => {
			if (quizType !== "standalone") {
				// only save the exempted students here if we're not creating a standalone assessment
				// (there is a seperate workflow to add exempted students during standalone creation)
				await saveProctoringExemptionStudents({
					assessmentInfo: { courseId: quizInfo?.courseId, quizId },
					students: getExemptedStudentsFromMap(uploadedExemptedStudentsMap, uploadedProfilesMap),
				});
			}
		},
		[quizInfo?.courseId, saveProctoringExemptionStudents, quizType, uploadedExemptedStudentsMap, uploadedProfilesMap],
	);

	const isExemptedStudentsModified = useMemo(() => {
		return hasExemptedStudentMapChanged(initialExemptedStudentsMapRef.current, uploadedExemptedStudentsMap);
	}, [uploadedExemptedStudentsMap]);

	const updateRoster = useCallback(
		(newRoster: ExemptionInfo) => {
			exemptionInfoRef.current = newRoster;
			setExemptionInfo(newRoster);
			if (newRoster.exemptRoster.size >= 0 && newRoster.nonExemptRoster.size >= 0) {
				setUploadedExemptedStudentsMap(newRoster.exemptRoster);
			}
		},
		[setUploadedExemptedStudentsMap],
	);

	const updateAccommodationView = useCallback((view?: ExemptionSectionView) => {
		setAccommodationView(view);
	}, []);

	const updateDraftInvalid = useCallback((invalid: boolean) => {
		setIsDraftInvalid(invalid);
	}, []);

	return {
		state: {
			exemptionInfo,
			exemptionInfoRef,
			numExempted,
			isExemptedStudentsModified,
			accommodationView,
			saveDraftAccommodationRef,
			isDraftInvalid,
		},
		action: {
			initStandaloneExemptionInfo,
			fetchAndSetExemptionInfo,
			updateRoster,
			handleSaveExemptedStudentsNotStandalone,
			updateAccommodationView,
			updateDraftInvalid,
			registerAccommodationSaveFn,
		},
	};
}
